import type { BulkResult } from '@/api/types'
import type { ToastVariant } from '@/components/ui'

export function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`
}

/** The first reason the server gave, so a dozen identical failures read as one. */
function firstReason(result: BulkResult): string | null {
  return result.failed.find((entry) => entry.detail)?.detail ?? null
}

export interface BulkSummary {
  variant: ToastVariant
  title: string
  description: string
}

/**
 * Turn a bulk response into something a person can act on.
 *
 * Every track becomes its own download job, so a request routinely lands as
 * "most of it worked". The summary always names what did not, rather than
 * reporting the happy path and letting the user discover the gap in Downloads.
 */
export function summariseBulk(result: BulkResult): BulkSummary {
  const failed = result.failed.length
  const reason = firstReason(result)

  if (result.queued > 0) {
    const notes: string[] = []
    if (result.already_present > 0) {
      notes.push(
        `${plural(result.already_present, 'song', 'songs')} ${
          result.already_present === 1 ? 'was' : 'were'
        } already in your library.`,
      )
    }
    if (failed > 0) {
      notes.push(`${plural(failed, 'song', 'songs')} could not be fetched.`)
      if (reason) notes.push(reason)
    }
    notes.push('Follow progress in Downloads.')

    return {
      variant: failed > 0 ? 'info' : 'success',
      title: `Queued ${plural(result.queued, 'song', 'songs')}`,
      description: notes.join(' '),
    }
  }

  if (failed > 0) {
    return {
      variant: 'error',
      title: `Could not queue ${plural(failed, 'song', 'songs')}`,
      description: reason ?? 'The music source did not return them.',
    }
  }

  if (result.already_present > 0) {
    return {
      variant: 'info',
      title: 'Nothing to download',
      description: `All ${plural(result.already_present, 'song', 'songs')} are already in your library.`,
    }
  }

  return {
    variant: 'info',
    title: 'Nothing was queued',
    description: result.detail ?? 'There was nothing left to download.',
  }
}
